import SEO from "@/components/feature/SEO";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { aboutData } from "@/mocks/about";

const orgSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "久久视频",
  url: typeof window !== "undefined" ? window.location.origin : "",
  description: aboutData.brandIntro,
  email: aboutData.contact.email,
  telephone: aboutData.contact.phone,
  address: {
    "@type": "PostalAddress",
    streetAddress: aboutData.contact.address,
  },
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: aboutData.faqs.map((faq) => ({
    "@type": "Question",
    name: faq.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: faq.answer,
    },
  })),
};

export default function About() {
  const breadcrumbItems = [
    { label: "首页", href: "/" },
    { label: "关于我们" },
  ];

  return (
    <>
      <SEO
        title="关于我们 - 久久视频 | 品牌介绍与联系方式"
        description="久久视频成立于2020年，国内领先的视频资源聚合平台。了解久久视频的品牌故事、发展历程、核心优势与联系方式。"
        keywords="久久视频,关于久久视频,视频平台,品牌介绍,联系方式"
        schemaData={[orgSchema, faqSchema]}
      />

      <main className="pt-20 pb-12">
        <div className="max-w-[1200px] mx-auto px-4 md:px-6">
          <Breadcrumb items={breadcrumbItems} />

          {/* Hero */}
          <section className="text-center py-12 md:py-16">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 font-heading mb-4">
              关于久久视频
            </h1>
            <p className="text-base md:text-lg text-foreground-500 max-w-3xl mx-auto leading-relaxed">
              {aboutData.brandIntro}
            </p>
          </section>

          {/* Mission & Vision */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-16">
            <div className="bg-accent-100/60 rounded-lg p-6 md:p-8 text-center">
              <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-200 text-accent-700">
                <i className="ri-flag-line text-xl"></i>
              </div>
              <h2 className="text-lg font-bold text-foreground-900 font-heading mb-2">我们的使命</h2>
              <p className="text-sm text-foreground-600">{aboutData.mission}</p>
            </div>
            <div className="bg-primary-50/60 rounded-lg p-6 md:p-8 text-center">
              <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-primary-100 text-primary-700">
                <i className="ri-eye-line text-xl"></i>
              </div>
              <h2 className="text-lg font-bold text-foreground-900 font-heading mb-2">我们的愿景</h2>
              <p className="text-sm text-foreground-600">{aboutData.vision}</p>
            </div>
          </section>

          {/* History Timeline */}
          <section className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading text-center mb-8">
              发展历程
            </h2>
            <div className="relative">
              <div className="absolute left-1/2 top-0 bottom-0 w-px bg-background-300 hidden md:block"></div>
              <div className="space-y-6">
                {aboutData.history.map((item, index) => (
                  <div
                    key={item.year}
                    className={`relative flex flex-col md:flex-row items-center gap-4 ${
                      index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"
                    }`}
                  >
                    <div className={`flex-1 ${index % 2 === 0 ? "md:text-right" : "md:text-left"} w-full`}>
                      <div className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                        <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-bold mb-2">
                          {item.year}
                        </span>
                        <h3 className="text-base font-bold text-foreground-900 font-heading">{item.event}</h3>
                        <p className="text-sm text-foreground-500 mt-1">{item.description}</p>
                      </div>
                    </div>
                    <div className="hidden md:flex w-8 h-8 rounded-full bg-primary-500 text-background-50 items-center justify-center shrink-0 relative z-10">
                      <i className="ri-check-line text-sm"></i>
                    </div>
                    <div className="flex-1 hidden md:block"></div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Features */}
          <section className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading text-center mb-2">
              为什么选择我们
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">六大核心优势，给您最好的观影体验</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {aboutData.features.map((feature) => (
                <div
                  key={feature.title}
                  className="bg-background-50 border border-background-200/70 rounded-lg p-5 text-center transition-all duration-300 hover:border-primary-300"
                >
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-700">
                    <i className={`${feature.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-base font-bold text-foreground-900 font-heading mb-1">{feature.title}</h3>
                  <p className="text-sm text-foreground-500">{feature.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Testimonials */}
          <section className="mb-16 bg-background-100 rounded-lg py-10 px-4 md:px-8">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading text-center mb-2">
              用户口碑
            </h2>
            <p className="text-sm text-foreground-500 text-center mb-8">听听用户们怎么说</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {aboutData.testimonials.map((t, i) => (
                <div key={i} className="bg-background-50 rounded-lg p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <img
                      src={t.avatar}
                      alt={t.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-semibold text-foreground-900">{t.name}</p>
                      <p className="text-xs text-foreground-400">{t.date}</p>
                    </div>
                    <div className="ml-auto flex items-center gap-0.5">
                      {Array.from({ length: 5 }).map((_, j) => (
                        <i
                          key={j}
                          className={`ri-star-fill text-xs ${j < t.rating ? "text-accent-500" : "text-foreground-200"}`}
                        ></i>
                      ))}
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{t.content}</p>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-16">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading text-center mb-8">
              常见问题
            </h2>
            <div className="max-w-3xl mx-auto space-y-3">
              {aboutData.faqs.map((faq, i) => (
                <details key={i} className="group bg-background-50 border border-background-200/70 rounded-lg">
                  <summary className="flex items-center justify-between p-5 cursor-pointer">
                    <h3 className="text-sm font-semibold text-foreground-900 pr-4">{faq.question}</h3>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </section>

          {/* Contact */}
          <section className="bg-background-100 rounded-lg p-8 md:p-10 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-2">
              联系我们
            </h2>
            <p className="text-sm text-foreground-500 mb-8">有任何问题或建议？欢迎通过以下方式联系我们</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { icon: "ri-mail-line", label: "邮箱", value: aboutData.contact.email },
                { icon: "ri-phone-line", label: "电话", value: aboutData.contact.phone },
                { icon: "ri-wechat-line", label: "微信", value: aboutData.contact.wechat },
                { icon: "ri-qq-line", label: "QQ", value: aboutData.contact.qq },
              ].map((item) => (
                <div key={item.label} className="bg-background-50 rounded-lg p-5">
                  <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-full bg-accent-100 text-accent-700">
                    <i className={`${item.icon} text-lg`}></i>
                  </div>
                  <p className="text-xs text-foreground-400 mb-1">{item.label}</p>
                  <p className="text-sm font-semibold text-foreground-900">{item.value}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-foreground-400 mt-6">
              地址：{aboutData.contact.address}
            </p>
          </section>
        </div>
      </main>
    </>
  );
}